import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { API_URL } from "../../../api";
import { useAuth } from "../../../auth/store";
import { useCan } from "../../../auth/roles";
import { CopyButton, PageHeader } from "../../../components/ui";
import { backendApi, backendKeys } from "../api";
import { BackToBackend } from "../components";

type Lang = "go" | "node" | "python" | "other";

const LANGS: Array<{ id: Lang; label: string }> = [
  { id: "go", label: "Go" },
  { id: "node", label: "Node.js" },
  { id: "python", label: "Python" },
  { id: "other", label: "Any other language" },
];

export function snippets(lang: Lang, endpoint: string): Array<{ title: string; code: string }> {
  const custom = endpoint.replace(/\/$/, "") !== "https://api.backstory.io";
  const envLine = `BACKSTORY_SERVER_KEY=sk_live_…${custom ? `\nBACKSTORY_ENDPOINT=${endpoint}` : ""}\nBACKSTORY_SERVICE=checkout\nBACKSTORY_ENVIRONMENT=production`;
  switch (lang) {
    case "go":
      return [
        { title: "Install", code: "go get backstory.io/go" },
        { title: "Set these where the service runs", code: envLine },
        {
          title: "Start it in main, and wrap your router",
          code: `import backstory "backstory.io/go"

func main() {
\tdefer backstory.Start(context.Background())()

\tmux := http.NewServeMux()
\t// …your routes…
\thttp.ListenAndServe(":8080", backstory.Handler(mux))
}`,
        },
        { title: "Check the setup", code: "go run backstory.io/go/cmd/backstory-doctor@latest" },
      ];
    case "node":
      return [
        { title: "Install", code: "npm i @backstory/node" },
        { title: "Set these where the service runs", code: envLine },
        { title: "Load it before anything else", code: `node --import @backstory/node/start server.js\n\n// or as the first line of your entry file:\nimport "@backstory/node/start";` },
        { title: "Check the setup", code: "npx backstory-doctor" },
      ];
    case "python":
      return [
        { title: "Install", code: "pip install backstory" },
        { title: "Set these where the service runs", code: envLine },
        { title: "Run your app through it", code: `backstory-run python app.py\n\n# or at the top of your entry module:\nimport backstory\nbackstory.start()` },
        { title: "Check the setup", code: "backstory-doctor" },
      ];
    default:
      return [
        {
          title: "Point any OpenTelemetry exporter here",
          code: `OTEL_EXPORTER_OTLP_ENDPOINT=${endpoint.replace(/\/$/, "")}/v1/otlp
OTEL_EXPORTER_OTLP_PROTOCOL=http/protobuf
OTEL_EXPORTER_OTLP_HEADERS=authorization=Bearer sk_live_…
OTEL_SERVICE_NAME=checkout
OTEL_RESOURCE_ATTRIBUTES=deployment.environment=production`,
        },
      ];
  }
}

export function ConnectPage() {
  const auth = useAuth();
  const can = useCan();
  const [lang, setLang] = useState<Lang>("go");
  const steps = snippets(lang, API_URL);

  return (
    <>
      <PageHeader title="Connect your backend" back={<BackToBackend />} />
      <div className="be-connect">
        <section className="card section">
          <h2 className="be-h2"><span className="be-step">1</span>Create a backend key</h2>
          <p className="muted">
            Backend keys live with the browser keys in{" "}
            {auth.currentOrgId ? <Link to="/orgs/$org/keys" params={{ org: auth.currentOrgId }}>SDK keys</Link> : "SDK keys"}.
            {can.admin ? " Create one there, then paste it below." : " An owner or admin creates them. Ask one of them for a key for your service."}
          </p>
        </section>

        <section className="card section">
          <h2 className="be-h2"><span className="be-step">2</span>Add Backstory to your service</h2>
          <div className="section-tabs be-lang-tabs" role="tablist" aria-label="Language">
            {LANGS.map((l) => (
              <button key={l.id} type="button" role="tab" aria-selected={lang === l.id} className={lang === l.id ? "active" : undefined} onClick={() => setLang(l.id)}>
                {l.label}
              </button>
            ))}
          </div>
          <div className="be-snippets" role="tabpanel">
            {steps.map((s) => (
              <div key={s.title} className="be-snippet">
                <div className="be-snippet-head">
                  <span className="small">{s.title}</span>
                  <CopyButton value={s.code} />
                </div>
                <pre className="code-block">{s.code}</pre>
              </div>
            ))}
          </div>
          <p className="muted small">
            Requests, database calls, queue jobs, and logs are picked up on their own. Requests that come from a recorded visit link to its replay automatically.
          </p>
        </section>

        <section className="card section">
          <h2 className="be-h2"><span className="be-step">3</span>Send a request</h2>
          <FirstData />
        </section>
      </div>
    </>
  );
}

function FirstData() {
  const auth = useAuth();
  const q = useQuery({
    queryKey: [...backendKeys.facets(auth.currentProjectId, "1h"), "connect"],
    queryFn: () => backendApi.facets("1h"),
    enabled: !!auth.currentProjectId,
    refetchInterval: (query) => ((query.state.data?.services.length ?? 0) > 0 ? false : 5000),
  });
  const services = q.data?.services ?? [];
  if (services.length === 0) {
    return (
      <p className="be-waiting" role="status" aria-live="polite">
        <span className="spinner" aria-hidden="true" /> Waiting for the first request from your service…
      </p>
    );
  }
  return (
    <div role="status" aria-live="polite">
      <p className="be-ok">Connected: {services.join(", ")}.</p>
      <Link to={"/backend" as never} className="btn primary">See requests</Link>
    </div>
  );
}
